import React, { useEffect, useState } from 'react';
import { fetchUserById } from '../../services/apiService';
import { decodedToken, removeAuthToken } from '../../utils/auth';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import permissionsConfig from '../../utils/permission.json';

const SkeletonLine = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-700 rounded ${className}`}></div>
);

const SkeletonLoader = () => (
  <div className="p-4 bg-gray-900 text-white min-h-screen">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center">
        <div className="w-5 h-6 bg-gray-700 rounded mr-8 animate-pulse"></div>
        <SkeletonLine className="h-5 w-40" />
      </div>
      <SkeletonLine className="h-8 w-16" />
    </div>

    <div className="mb-2">
      <SkeletonLine className="h-4 w-16 mb-2" />
    </div>
    <div className="mb-4">
      <SkeletonLine className="h-4 w-32" />
    </div>

    <div className="mb-2">
      <SkeletonLine className="h-4 w-24 mb-2" />
    </div>
    <div className="mb-4">
      <SkeletonLine className="h-4 w-48" />
    </div>

    <div className="mb-2">
      <SkeletonLine className="h-4 w-28 mb-2" />
    </div>
    <div className="mb-4">
      <SkeletonLine className="h-4 w-40" />
    </div>

    <div className="mb-2">
      <SkeletonLine className="h-4 w-32 mb-2" />
    </div>
    <div className="mb-4">
      <SkeletonLine className="h-4 w-12 mb-1" />
      <SkeletonLine className="h-4 w-16" />
    </div>

    <div className="mb-2">
      <SkeletonLine className="h-4 w-36 mb-2" />
    </div>
    <div className="mb-12">
      <SkeletonLine className="h-4 w-20 mb-1" />
      <SkeletonLine className="h-4 w-16" />
    </div>

    {[...Array(6)].map((_, index) => (
      <div key={index} className="mb-4">
        <SkeletonLine className="h-4 w-64 mb-1" />
        <SkeletonLine className="h-4 w-16" />
      </div>
    ))}
  </div>
);

const AccountInfo = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const displayNameMapping = {
    "powerAdmin": "Power Admin",
    "admin": "Admin",
    "obmExpert": "OBM Expert",
    "obmRD": "OBM RD",
    "obmQM": "OBM QM",
    "gpm": "GPM",
    "odm": "ODM"
  };

  const PermissionMapper = {
    AiOpticalDetectionSystemAiOds: "AI Optical Detection System (AI-ODS)",
    AudioIssueVisualization: "AI Audio Detection System (AI-ADS)",
    LLMuraSpotLineBandIssueLabeling: "LL Mura Spot Line Band Issue Labeling",
    MuraSpotLineBandIssueDetection: "Mura Spot Line Band Issue Detection",
    StatisticsAndAnalysis: "Statistics and Analysis",
    HWChangeVerificationAssessment: "HW Change Verification Assessment",
    SWChangeVerificationAssessment: "SW Change Verification Assessment",
    Management: "Management",
    templateManagement: "Template Management",
    photoManagement: "Photo Management"
  };

  const getAvailablePermissions = (userType) => {
    if (!userType) return [];
    
    return Object.keys(permissionsConfig).filter(permission => 
      permissionsConfig[permission].hasOwnProperty(userType)
    );
  };

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const tokenData = decodedToken();
        if (!tokenData || !tokenData.email) {
          setError("No user is logged in or invalid token");
          setLoading(false);
          return;
        }

        const currentUser = await fetchUserById(tokenData.email); 
        console.log("Fetched User Data:", currentUser);

        const availablePermissions = getAvailablePermissions(currentUser.userType);

        const permissionsData = {};
        availablePermissions.forEach(permissionKey => {
          permissionsData[permissionKey] = currentUser.permissions?.[permissionKey] ? "Enabled" : "Disabled";
        });

        setUserData({
          name: currentUser.userId,
          email: currentUser.email,
          company: currentUser.companyInfo,
          status: currentUser.isAccountActive === "active" ? "Active" :
                  currentUser.isAccountActive === "suspended" ? "Suspended" : "Deleted",
          type: currentUser.userType,
          displayType: displayNameMapping[currentUser.userType] || currentUser.userType,
          permissions: permissionsData,
          availablePermissions: availablePermissions
        });

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("email");
    removeAuthToken();
    navigate('/');
  };

  if (loading) return <SkeletonLoader />;
  
  if (error) {
    return (
      <div className="p-4 bg-gray-900 text-white min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-400 text-lg mb-2">⚠️ Error</div>
            <div className="text-gray-300">{error}</div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!userData) {
    return (
      <div className="p-4 bg-gray-900 text-white min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-yellow-400 text-lg mb-2">📭</div>
            <div className="text-gray-300">No user data available.</div>
          </div>
        </div>
      </div>
    );
  }

  const permissions = userData.availablePermissions.map(permissionKey => ({
    label: PermissionMapper[permissionKey] || permissionKey.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase()),
    value: userData.permissions[permissionKey]
  }));


  const filteredPermissions = permissions.filter(perm => perm.value === "Enabled");

  return (
    <div className="p-4 bg-gray-900 text-white min-h-screen">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <button className="mr-8" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-6" />
          </button>
          <h2 className="text-md font-semibold text-white">Account Information</h2>
        </div>
        <button
          className="bg-red-600  px-3 py-2 rounded-md hover:bg-red-700 transition-colors"
          onClick={handleLogout}
        >
          Logout
        </button>
      </div>

      <div className="mb-2"><strong className="text-md font-semibold text-white">User ID</strong></div>
      <div className="mb-4 text-gray-300 text-sm">{userData.name}</div>

      <div className="mb-2"><strong className="text-md font-semibold text-white">Email Address</strong></div>
      <div className="mb-4 text-gray-300 text-sm">{userData.email}</div>

      <div className="mb-2"><strong className="text-md font-semibold text-white">Company Info</strong></div>
      <div className="mb-4 text-gray-300 text-sm">Company: {userData.company}</div>

      <div className="mb-2"><strong className="text-md font-semibold text-white">Account Status</strong></div>
      <div className="mb-4 text-sm">
        <span>Status</span>
        <div className="text-gray-400 text-sm">{userData.status}</div>
      </div>

      <div className="mb-2"><strong className="text-md font-semibold text-white">Permission Status</strong></div>
      <div className="mb-4 text-sm">
        <span>User Type</span>
        <div className="mb-4 text-gray-400 text-sm">{userData.displayType}</div>
      </div>

      {filteredPermissions.length > 0 ? (
        filteredPermissions.map((perm, index) => (
          <div key={index} className="mb-4">
            <span>{perm.label}</span>
            <div className="text-gray-400 text-sm">{perm.value}</div>
          </div>
        ))
      ) : (
        <div className="text-gray-400 text-sm">No enabled permissions available.</div>
      )}
    </div>
  );
};

export default AccountInfo;